import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  DodFilterKey,
  DodFilters,
  DodRangePreset,
  DodRefreshOption,
  DodResolvedRange,
  UseDodFiltersResult,
} from './useDodFilters.types';

const RANGE_PRESETS: DodRangePreset[] = ['15m', '1h', '6h', '24h', '7d', 'custom'];
const REFRESH_OPTIONS: DodRefreshOption[] = ['off', '10s', '30s', '1m'];
const FILTER_KEYS: DodFilterKey[] = ['rcp', 'nlk', 'cmd', 'status'];

export const DOD_DEFAULT_RANGE: DodRangePreset = '1h';
export const DOD_DEFAULT_REFRESH: DodRefreshOption = '10s';

const RANGE_DURATION_MS: Record<Exclude<DodRangePreset, 'custom'>, number> = {
  '15m': 15 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
};

const REFRESH_INTERVAL_MS: Record<DodRefreshOption, number | false> = {
  off: false,
  '10s': 10_000,
  '30s': 30_000,
  '1m': 60_000,
};

/** Step (in seconds) the backend rollups should use for a given window. Mirrors design §3.2. */
export const rangeStepSeconds = (range: DodRangePreset): number => {
  if (range === '15m') {
    return 10;
  }

  if (range === '1h' || range === '6h') {
    return 60;
  }

  return 300;
};

const parseTimestamp = (value: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = Date.parse(value);

  return Number.isNaN(parsed) ? undefined : parsed;
};

export const resolveDodRange = (range: DodRangePreset, from: string, to: string): DodResolvedRange => {
  if (range === 'custom') {
    const toTs = parseTimestamp(to) ?? Date.now();
    const fromTs = parseTimestamp(from) ?? toTs - RANGE_DURATION_MS['1h'];

    return fromTs <= toTs ? { fromTs, toTs } : { fromTs: toTs, toTs: fromTs };
  }

  const toTs = Date.now();

  return { fromTs: toTs - RANGE_DURATION_MS[range], toTs };
};

/**
 * Single source of truth for the DOD monitor's time range, refresh interval and filters.
 *
 * Everything lives in the query string so a link reproduces the view, the way a Grafana URL does,
 * and so switching tabs keeps the selection without any shared React state. Writes merge into the
 * existing params (`useSearchParams`'s setter replaces the whole string) and use `replace` so a
 * ticking refresh selector does not fill the browser history.
 */
export const useDodFilters = (): UseDodFiltersResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const rangeParam = searchParams.get('range') ?? '';
  const range: DodRangePreset = RANGE_PRESETS.includes(rangeParam as DodRangePreset)
    ? (rangeParam as DodRangePreset)
    : DOD_DEFAULT_RANGE;

  const refreshParam = searchParams.get('refresh') ?? '';
  const refresh: DodRefreshOption = REFRESH_OPTIONS.includes(refreshParam as DodRefreshOption)
    ? (refreshParam as DodRefreshOption)
    : DOD_DEFAULT_REFRESH;

  const customFrom = searchParams.get('from') ?? '';
  const customTo = searchParams.get('to') ?? '';

  const filters = useMemo<DodFilters>(
    () =>
      FILTER_KEYS.reduce<DodFilters>(
        (accumulated, key) => ({ ...accumulated, [key]: searchParams.get(key) ?? '' }),
        {} as DodFilters,
      ),
    [searchParams],
  );

  const update = useCallback(
    (patch: Record<string, string>): void => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          Object.entries(patch).forEach(([key, value]) => {
            if (value) {
              next.set(key, value);
            } else {
              next.delete(key);
            }
          });

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setRange = useCallback<UseDodFiltersResult['setRange']>(
    (nextRange, custom) => {
      if (nextRange !== 'custom') {
        update({ range: nextRange, from: '', to: '' });

        return;
      }

      update({
        range: 'custom',
        from: custom?.from ? custom.from.toISOString() : customFrom,
        to: custom?.to ? custom.to.toISOString() : customTo,
      });
    },
    [update, customFrom, customTo],
  );

  const setRefresh = useCallback<UseDodFiltersResult['setRefresh']>(
    (nextRefresh) => update({ refresh: nextRefresh }),
    [update],
  );

  const setFilter = useCallback<UseDodFiltersResult['setFilter']>((key, value) => update({ [key]: value }), [update]);

  const clear = useCallback(() => update({ rcp: '', nlk: '', cmd: '', status: '' }), [update]);

  const resolveRange = useCallback(() => resolveDodRange(range, customFrom, customTo), [range, customFrom, customTo]);

  const { fromTs, toTs } = resolveRange();

  const queryString = useMemo(() => {
    const echo = new URLSearchParams();
    echo.set('range', range);

    if (range === 'custom') {
      customFrom && echo.set('from', customFrom);
      customTo && echo.set('to', customTo);
    }

    echo.set('refresh', refresh);
    FILTER_KEYS.forEach((key) => filters[key] && echo.set(key, filters[key]));

    return `?${echo.toString()}`;
  }, [range, refresh, filters, customFrom, customTo]);

  return {
    range,
    refresh,
    refreshMs: REFRESH_INTERVAL_MS[refresh],
    filters,
    hasFilters: FILTER_KEYS.some((key) => !!filters[key]),
    fromTs,
    toTs,
    resolveRange,
    customFrom,
    customTo,
    setRange,
    setRefresh,
    setFilter,
    clear,
    queryString,
  };
};
